import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { createRendering } from '@/rendering';
import Alpine from 'alpinejs';
import { createRoom, updateCulling } from '@/room';
import { initSession, scheduleSave } from '@/session';
import {
  initFurniture, placeNewItem, restoreItem, swapVariant,
  removeFurniture, getObject, setOutlineEnabled,
} from '@/furniture';
import { initSidebar } from '@/sidebar';
import { buildCheckoutUrl, buildCartItems } from '@/checkout';
import { getLocale, formatMeasurement } from '@/formatting';
import { initInteraction, syncOutline, setInteractionEnabled } from '@/interaction';
import {
  WALL_DEFS, wallFromRotation, computeFlushPosition,
  computeSlideBounds, computeYBounds,
} from '@/dragging';

const section = document.querySelector('#wardrobe-configurator');
if (section) section.lang = getLocale();

// ── Room state (cm) ────────────────────────────────────────

let roomWidth = 485;
let roomDepth = 400;
let roomHeight = 248;
let sessionId = null;

// ── Room customization mode ────────────────────────────────

let isRoomMode = false;

// ── Viewport ────────────────────────────────────────────────

const container = document.querySelector('#wardrobe-configurator .wardrobe-viewport');

// ── Scene ───────────────────────────────────────────────────

const scene = new THREE.Scene();

// ── Camera ──────────────────────────────────────────────────

const camera = new THREE.PerspectiveCamera(
  45,
  container.clientWidth / container.clientHeight,
  1,
  5000,
);
// Front-left, ~35° above horizontal
camera.position.set(-266, 373, 461);

// ── Room and rendering ──────────────────────────────────────

const roomHandle = createRoom(scene, {
  width: roomWidth,
  depth: roomDepth,
  height: roomHeight,
});
const rendering = createRendering({ container, scene, camera, roomHandle });
const { renderer, outlinePass } = rendering;

// ── OrbitControls ───────────────────────────────────────────

const controls = new OrbitControls(camera, renderer.domElement);
controls.target.set(0, 100, 0);
controls.minPolarAngle = 0;
controls.maxPolarAngle = Math.PI * 0.6;
controls.minDistance = 200;
controls.maxDistance = 1200;
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.update();

function clampControls() {
  const halfW = roomWidth / 2;
  const halfD = roomDepth / 2;
  const t = controls.target;
  t.x = THREE.MathUtils.clamp(t.x, -halfW, halfW);
  t.y = THREE.MathUtils.clamp(t.y, 0, roomHeight);
  t.z = THREE.MathUtils.clamp(t.z, -halfD, halfD);

  // Keep camera above floor regardless of polar angle + distance
  if (camera.position.y < 10) {
    camera.position.y = 10;
  }
}

// ── Resize ──────────────────────────────────────────────────

const resizeObserver = new ResizeObserver(() => {
  rendering.resize();
  if (isRoomMode) updateTopDownCamera();
});
resizeObserver.observe(container);

// ── Room dimension updates ──────────────────────────────────

function applyRoomDimensions(width, depth, height) {
  roomWidth = width;
  roomDepth = depth;
  roomHeight = height;

  roomHandle.rebuild({ width, depth, height });

  rendering.setRoomDimensions({ width, depth, height });
}

// ── Reflush all items to walls after room resize ───────────
// Reposition every item: flush against its wall, clamp slide + Y to new bounds.

function reflushAllItems(items) {
  const dims = { width: roomWidth, depth: roomDepth, height: roomHeight };

  for (const item of items) {
    const obj = getObject(item.id);
    if (!obj) continue;

    const bbox = obj.userData.bboxAtOrigin;
    if (!bbox) continue;

    const wall = wallFromRotation(obj.rotation.y);
    const def = WALL_DEFS[wall];

    // 1. Reflush perpendicular to wall
    obj.position[def.flushAxis] = computeFlushPosition(wall, bbox, dims);

    // 2. Clamp Y to floor/ceiling
    const yBounds = computeYBounds(bbox, dims);
    obj.position.y = Math.max(yBounds.min, Math.min(yBounds.max, obj.position.y));

    // 3. Clamp slide axis to wall ends
    const slideBounds = computeSlideBounds(wall, bbox, dims);
    obj.position[def.slideAxis] = Math.max(slideBounds.min, Math.min(slideBounds.max, obj.position[def.slideAxis]));

    // Write back to Alpine item for persistence
    item.pos_x = obj.position.x;
    item.pos_y = obj.position.y;
    item.pos_z = obj.position.z;
    item.wall = wall;
  }
}

// ── Top-down camera ────────────────────────────────────────

const ROOM_MODE_FOV = 5;    // very narrow FOV ≈ orthographic projection
const DEFAULT_FOV = 45;

function computeTopDownHeight(w, d) {
  const aspect = container.clientWidth / container.clientHeight;
  const halfTan = Math.tan((ROOM_MODE_FOV * Math.PI / 180) / 2);
  const hForDepth = d / (2 * halfTan);
  const hForWidth = w / (2 * halfTan * aspect);
  return Math.max(hForDepth, hForWidth) * 1.5; // 50% margin — room at ~65% of viewport
}

function updateTopDownCamera() {
  if (!isRoomMode) return;
  const h = computeTopDownHeight(roomWidth, roomDepth);
  camera.position.set(0, h, 0.01);
  controls.target.set(0, 0, 0);
}

// ── Enter / exit room customization view ───────────────────

const FADE_MS = 250;
let _fadeTimer = null;  // cancel stale fade callbacks on rapid toggle

function enterRoomView() {
  if (_fadeTimer) { clearTimeout(_fadeTimer); _fadeTimer = null; }

  controls.enabled = false;
  isRoomMode = true;
  setInteractionEnabled(false);
  setOutlineEnabled(false);

  // Fade out, snap camera, fade in
  renderer.domElement.style.transition = `opacity ${FADE_MS}ms ease`;
  renderer.domElement.style.opacity = '0';

  _fadeTimer = setTimeout(() => {
    _fadeTimer = null;
    camera.fov = ROOM_MODE_FOV;
    camera.far = 20000;
    camera.updateProjectionMatrix();
    const h = computeTopDownHeight(roomWidth, roomDepth);
    camera.position.set(0, h, 0.01);
    controls.target.set(0, 0, 0);
    camera.lookAt(controls.target);

    wallDimContainer.style.display = 'block';
    dimLabels.style.display = 'none';

    renderer.domElement.style.opacity = '1';
  }, FADE_MS);
}

function computeDefaultCamera() {
  // Scale the default orbit position proportionally to room size.
  // Reference: (-266, 373, 461) for a 485×400×248 room.
  const scale = (Math.max(roomWidth, roomDepth) / 485) * 1.3;
  return {
    pos: new THREE.Vector3(-266 * scale, 373 * scale, 461 * scale),
    target: new THREE.Vector3(0, roomHeight * 0.4, 0),
  };
}

function exitRoomView() {
  if (_fadeTimer) { clearTimeout(_fadeTimer); _fadeTimer = null; }

  setOutlineEnabled(true);
  triggerSave();

  wallDimContainer.style.display = 'none';

  // Fade out, snap camera, fade in
  renderer.domElement.style.transition = `opacity ${FADE_MS}ms ease`;
  renderer.domElement.style.opacity = '0';

  _fadeTimer = setTimeout(() => {
    _fadeTimer = null;
    const cam = computeDefaultCamera();
    camera.fov = DEFAULT_FOV;
    camera.far = 5000;
    camera.updateProjectionMatrix();
    camera.position.copy(cam.pos);
    controls.target.copy(cam.target);
    camera.lookAt(cam.target);
    controls.update();
    controls.enabled = true;
    isRoomMode = false;
    setInteractionEnabled(true);

    renderer.domElement.style.opacity = '1';
  }, FADE_MS);
}

// ── Room getters (for dependency injection) ─────────────────

function getRoomDims() {
  return { width: roomWidth, depth: roomDepth, height: roomHeight };
}

// ── Save (reads from Alpine store) ──────────────────────────

function triggerSave() {
  const store = Alpine.store('app');
  scheduleSave(sessionId, {
    room: { width: roomWidth, depth: roomDepth, height: roomHeight },
    items: store ? store.serializeItems() : [],
  });
}

// ── Dimension labels ────────────────────────────────────────

const dimLabels = container.querySelector('.wardrobe-dimension-labels');
const dimWidth = dimLabels.querySelector('.wardrobe-dim-width');
const dimHeight = dimLabels.querySelector('.wardrobe-dim-height');
const dimDepth = dimLabels.querySelector('.wardrobe-dim-depth');

// Wall dimension labels (room customization mode)
const wallDimContainer = container.querySelector('.wardrobe-wall-dim-labels');
const wallDimBack = wallDimContainer.querySelector('.wardrobe-wall-dim-back');
const wallDimFront = wallDimContainer.querySelector('.wardrobe-wall-dim-front');
const wallDimLeft = wallDimContainer.querySelector('.wardrobe-wall-dim-left');
const wallDimRight = wallDimContainer.querySelector('.wardrobe-wall-dim-right');
const _wallLabelPos = new THREE.Vector3();
const _projVec = new THREE.Vector3();
const _dimBbox = new THREE.Box3();
const _widthAnchor = new THREE.Vector3();
const _heightAnchor = new THREE.Vector3();
const _depthAnchor = new THREE.Vector3();

function projectToScreen(worldPos) {
  _projVec.copy(worldPos);
  _projVec.project(camera);
  const rect = container.getBoundingClientRect();
  return {
    x: ( _projVec.x * 0.5 + 0.5) * rect.width,
    y: (-_projVec.y * 0.5 + 0.5) * rect.height,
  };
}

function updateDimensionLabels() {
  const store = Alpine.store('app');
  const selectedId = store?.selectedId;

  if (!selectedId) {
    dimLabels.style.display = 'none';
    return;
  }

  const obj = getObject(selectedId);
  if (!obj) {
    dimLabels.style.display = 'none';
    return;
  }

  const bbox = _dimBbox.setFromObject(obj);

  const w = bbox.max.x - bbox.min.x;
  const h = bbox.max.y - bbox.min.y;
  const d = bbox.max.z - bbox.min.z;

  dimLabels.style.display = 'block';

  // Width label: front-bottom center edge
  _widthAnchor.set(
    (bbox.min.x + bbox.max.x) / 2,
    bbox.min.y,
    bbox.max.z,
  );
  const wp = projectToScreen(_widthAnchor);
  dimWidth.style.left = wp.x + 'px';
  dimWidth.style.top = (wp.y + 14) + 'px';
  dimWidth.textContent = formatMeasurement(w, { round: true });
  dimWidth.style.display = '';

  // Height label: left-side midpoint
  _heightAnchor.set(
    bbox.min.x,
    (bbox.min.y + bbox.max.y) / 2,
    (bbox.min.z + bbox.max.z) / 2,
  );
  const hp = projectToScreen(_heightAnchor);
  dimHeight.style.left = (hp.x - 14) + 'px';
  dimHeight.style.top = hp.y + 'px';
  dimHeight.textContent = formatMeasurement(h, { round: true });
  dimHeight.style.display = '';

  // Depth label: right-front midpoint
  _depthAnchor.set(
    bbox.max.x,
    bbox.min.y,
    (bbox.min.z + bbox.max.z) / 2,
  );
  const dp = projectToScreen(_depthAnchor);
  dimDepth.style.left = (dp.x + 14) + 'px';
  dimDepth.style.top = dp.y + 'px';
  dimDepth.textContent = formatMeasurement(d, { round: true });
  dimDepth.style.display = '';
}

// ── Wall dimension labels (room mode) ───────────────────────

function positionWallLabel(el, worldX, worldY, worldZ, text) {
  _wallLabelPos.set(worldX, worldY, worldZ);
  const sp = projectToScreen(_wallLabelPos);
  el.style.left = sp.x + 'px';
  el.style.top = sp.y + 'px';
  el.textContent = text;
}

function updateWallLabels() {
  const hw = roomWidth / 2;
  const hd = roomDepth / 2;
  const wText = formatMeasurement(roomWidth, { round: true });
  const dText = formatMeasurement(roomDepth, { round: true });
  positionWallLabel(wallDimBack,  0, 0, -hd - 20, wText);
  positionWallLabel(wallDimFront, 0, 0,  hd + 20, wText);
  positionWallLabel(wallDimLeft, -hw - 20, 0, 0, dText);
  positionWallLabel(wallDimRight, hw + 20, 0, 0, dText);
}

// ── Animation loop ──────────────────────────────────────────

function animate(now) {
  if (!section.isConnected) {
    resizeObserver.disconnect();
    controls.dispose();
    rendering.dispose();
    return;
  }
  requestAnimationFrame(animate);

  if (!isRoomMode) {
    controls.update();
    clampControls();
  }

  updateCulling(camera, roomHandle);
  rendering.render({ now, loading: Alpine.store('app')?.isLoading ?? true });

  if (isRoomMode) updateWallLabels();
  else updateDimensionLabels();
}
requestAnimationFrame(animate);

// ── Initialization (async, after scene is rendering) ────────

initSession().then(async (session) => {
  sessionId = session.id;

  const r = session.room;
  if (r.width !== roomWidth || r.depth !== roomDepth || r.height !== roomHeight) {
    applyRoomDimensions(r.width, r.depth, r.height);
  }

  // Initialize furniture module with scene reference + outline pass
  initFurniture(scene, outlinePass);

  // Make section.settings strings (injected via __LB_STRINGS__) available
  // to Alpine x-text/x-bind expressions as $strings.<key>.
  Alpine.magic('strings', () => window.__LB_STRINGS__);

  // Initialize Alpine sidebar (fetches catalog, starts Alpine)
  await initSidebar(Alpine, {
    placeNewItem,
    swapVariant,
    removeFurniture,
    triggerSave,
    getRoomDims,
    syncOutline,
    applyRoomDimensions,
    enterRoomView,
    exitRoomView,
    updateTopDownCamera,
    reflushAllItems,
    buildCheckoutUrl,
    buildCartItems,
  });

  // Initialize click + drag interaction on the canvas
  const store = Alpine.store('app');
  initInteraction({
    canvas: renderer.domElement,
    camera,
    controls,
    getItems: () => store.items,
    onSelect: (itemId) => store.selectItem(itemId),
    onDeselect: () => store.deselectAll(),
    onDragEnd: () => triggerSave(),
    roomHandle,
    getRoomDims: () => getRoomDims(),
  });

  // Restore saved items from session
  for (const savedItem of session.items) {
    const item = {
      id: crypto.randomUUID(),
      ...savedItem,
      loading: false,
      _swapVersion: 0,
    };
    store.items.push(item);
    store.isLoading = true;
    try {
      await restoreItem(store.items[store.items.length - 1]);
    } catch (err) {
      console.error('Failed to restore item:', err);
      // Remove the broken item so the rest of the session still works
      store.items = store.items.filter((i) => i.id !== item.id);
    } finally {
      store.isLoading = false;
    }
  }
});
